/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { referralCodeAtom } from '@root/entities/referral'
import { reatomChangeReferralCodeForm } from '@root/features/referral/atoms/reatom-change-referral-code'
import { useTranslation } from '@root/shared/localization'
import { Dialog, Separator } from '@root/shared/ui-kit'
import { DialogAnnouncement } from '@root/shared/ui-kit/dialog/dialog-announcement'

import { ReferralCodeForm } from '../common/referral-code-form'
import { ReferralCodeInfo } from '../common/referral-code-info'

import type { ReferralCodeSubmitValues } from '../common/referral-code-form'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const ChangeReferralCodeDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation()
  const model = useMemo(() => reatomChangeReferralCodeForm(), [])
  const [requestState] = useAtom(model.requestState)
  const execute = useAction(model.change)
  const [currentReferralCode] = useAtom(referralCodeAtom)

  const handleSubmit = (values: ReferralCodeSubmitValues) => {
    execute({
      referralCode: values.referralCode,
      walletId: values.wallet.id,
    })
  }

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onOpenChange(false)
    }
  }, [requestState, onOpenChange])

  return (
    <Root
      withClose
      open={open}
      onOpenChange={onOpenChange}
      describedBy={'change-referral-code'}
    >
      <DialogAnnouncement
        css={css`
          --announcement-header-background: var(--appearanceA-7);
          --announcement-body-background: var(--background-1);
        `}
        withSeparator
        header={
          <Header>
            <Title>{t('referral.change-referral-code')}</Title>
            {currentReferralCode ? (
              <ReferralCodeInfo
                referralCode={currentReferralCode}
                withLabel
                css={css`
                  margin-block: 0 10px;
                `}
              />
            ) : undefined}
            <Separator />
            <Note>{t('referral.change-referral-note')}</Note>
          </Header>
        }
      >
        <Form
          device="desktop"
          onCancel={() => onOpenChange(false)}
          existingReferralCode={currentReferralCode}
          state={requestState}
          onSubmit={handleSubmit}
        />
      </DialogAnnouncement>
    </Root>
  )
}

const Root = styled(Dialog)`
  width: 400px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 12px 16px;
  position: relative;
  text-align: center;
  gap: 16px;
`

const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  line-height: normal;
  line-height: 140%;
`

const Form = styled(ReferralCodeForm)`
  padding: 20px 12px;
`
const Note = styled.div`
  color: var(--textA-9);
  text-align: center;

  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`
